/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatRupiah, removeHTML, sendAnalyticsEvent } from '../../utils/helper';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/solid';
import Notes from '../../components/Notes/index';
import { PencilEditIcon } from '../../utils/svg';
import ImageLoader from '../ImageLoader';
import * as Yup from 'yup';
import { useFormik } from 'formik';

export const ComboDetail = ({ product = {}, item = null, handleSubmitItem = (data) => {}, isEdit = false }) => {
	const desc = removeHTML(product?.short_description || '');
	const containerRef = useRef(null)
	const [total, setTotal] = useState(1);
	const [note, setNote] = useState('');

	const formik = useFormik({
		initialValues: product?.comboset?.map((combo) => ({
			...combo,
			productDetails: [],
		})),
		validateOnChange: false,
		onSubmit: (values) => {
			handleSubmit(values);
		},
	});

	const { errors, touched, values, setFieldValue, setValues, handleSubmit: handleSubmitFormik } = formik;

	const scrollInto = (container, target) => {
		if(container && target) {
			container.scrollTo({
				top: target.offsetTop - container.offsetTop,
				behavior: 'smooth',
			});
		}
	}

	const validateComboStepByStep = async () => {
		const container = containerRef.current;

		for (let i = 0; i < values.length; i++) {
			const combo = values[i];
			const selectedCount = combo?.productDetails?.length || 0;
			const min = Number(combo?.min_select ?? 0);
			const max = Number(combo?.max_select ?? 0);
			const name = combo?.combo_name ?? '';

			let errorMessage = '';

			if (min && selectedCount < min) {
				errorMessage = `Pilih ${min} ${name}`;
			} else if (max && selectedCount > max) {
				errorMessage = `Maksimal ${max} ${name}`;
			}

			if (errorMessage) {
				await setFieldValue(`${i}.productDetails`, combo?.productDetails, false);
				formik.setFieldTouched(`${i}.productDetails`, true, false);
				formik.setFieldError(`${i}.productDetails`, errorMessage);

				const target = container?.querySelector(`#combo-${combo?.combo_id}`);
				scrollInto(container, target);

				return false;
			}
		}

		return true;
	};

	const handleSubmit = async (values) => {
		console.log('handleSubmit');
		const comboSet = values?.map((combo) => {
			return {
				comboSetId: combo?.combo_id,
				comboSetname: combo?.combo_name,
				productDetails: combo?.productDetails?.map((detail) => {
					return {
            productPrimaryID: detail?.product_primary_id,
						productID: detail?.product_id,
						productName: detail?.product_name,
						productSKU: detail?.product_sku,
						quantity: 1,
					};
				}),
				min_max_flag: combo?.max_select || 1,
			};
		});

		const dataPost = {
			productID: product?.product_id,
			quantity: total,
			type: !isEdit ? 'add' : 'update',
			productType: 'Combo',
			specialNotes: note,
			comboset: comboSet,
			itemID: !isEdit ? null : item?.itemID,
      productPrimaryID: product?.product_primary_id
		};
		handleSubmitItem(dataPost);
	};

	useEffect(() => {
		if (!isEdit) return;
		setTotal(Number(item?.itemQuantity || 1));
		setNote(item?.itemNotes || '');
		const updatedComboSet =
			item?.comboset?.map((combo, index) => {
				return {
					...product?.comboset?.[index],
					productDetails: combo?.productDetails.map((detail, idx) => {
						return {
              product_primary_id: detail?.product_primary_id,
							product_id: detail?.productID,
							product_name: detail?.productName,
							product_sku: detail?.productSKU,
							quantity: 1,
						};
					}),
				};
			}) || [];
		setValues(updatedComboSet);
	}, [item]);

	return (
		<>
			<div ref={containerRef} data-body-scroll-lock-ignore="true" className={`bg-white rounded-t-xl p-4 shadow-custom transition-transform duration-500 ease-in-out z-50 h-full max-h-[60vh] overflow-y-scroll scrollbar-hide ${true ? 'translate-y-0 relative' : 'translate-y-full hidden'}`}>
				<div className='hidden justify-center'>
					<div className='bg-gray-300 h-1.5 w-10 rounded-full' />
				</div>
				<div className='relative mt-4 h-44'>
					<ImageLoader alt={product?.product_slug} src={product?.product_gallery ?? product?.product_thumbnail} />
				</div>
				<div className='mt-4 text-gray-900 font-semibold text-sm'>{product?.product_name || ''}</div>
				<div className='text-sm flex items-center pt-2'>
					<div className='font-semibold pr-1 text-gray-800'>{formatRupiah(product?.product_price)}</div>
					{Boolean(Number(product?.product_cost)) && <div className='line-through text-gray-500'>{formatRupiah(Number(product?.product_cost))}</div>}
				</div>
				<div className='text-gray-400 text-xs pt-2 font-normal'>{desc}</div>
				<div className='mt-5'>
					{values?.map((combo, index) => {
						return (
							<div key={index} id={`combo-${combo?.combo_id}`}>
								<div className='text-sm text-gray-900 font-semibold my-4'>{combo?.combo_name}</div>
								<div className='text-red-500 text-xs'>{errors?.[index]?.productDetails && touched?.[index]?.productDetails && errors?.[index]?.productDetails}</div>
								{combo?.products?.map((comboProduct, idx) => {
									const comboPrice = Number(comboProduct?.product_price) || 0;
									const isMultipleSelection = Number(combo?.max_select) > 1 || Number(combo?.min_select) > 1;
									const isSelected =
										combo?.productDetails?.some((selectedCombo) => {
											const isMatching = selectedCombo.product_id === comboProduct?.product_id;
											return isMatching;
										}) ?? false;
									if (isMultipleSelection) {
										return (
											<label key={`${index}_${idx}_${combo.combo_id}`} htmlFor={`${index}_${idx}_${combo.combo_id}`} className='flex items-center justify-between h-12 border-b-1 border-gray-200 border-solid'>
												<div htmlFor={`${index}_${idx}_${combo.combo_id}`} className='text-xs text-gray-900 font-semibold flex'>
													{comboProduct?.product_name} {Boolean(comboPrice) && <div className='text-gray-500 text-xs font-normal pl-2'>{`+${formatRupiah(comboPrice)}`}</div>}
												</div>
												<input
													id={`${index}_${idx}_${combo.combo_id}`}
													type={'checkbox'}
													value={comboProduct?.product_id}
													name={`combo_${index}`}
													className='w-4 h-4  bg-white !rounded-none !border-[var(--byzantium)] border-1 focus:!shadow-none checked:bg-[var(--byzantium)] checked:border-[var(--byzantium)] hover:[var(--byzantium)] !focus:ring-1 !focus:ring-[var(--byzantium)]'
													onChange={(e) => {
														if (e.target.checked) {
															setFieldValue(`${index}.productDetails`, [comboProduct, ...combo?.productDetails]);
														} else {
															const updatedDetails = combo?.productDetails?.filter((product) => product.product_id !== comboProduct.product_id);
															setFieldValue(`${index}.productDetails`, updatedDetails);
														}
													}}
													checked={isSelected}
												/>
											</label>
										);
									}
									return (
                    <label key={`${index}_${idx}_${combo.combo_id}`} htmlFor={`${index}_${idx}_${combo.combo_id}`} className='flex items-center justify-between h-12 border-b-1 border-gray-200 border-solid'>
                      <div htmlFor={`${index}_${idx}_${combo.combo_id}`} className='text-xs text-gray-900 font-semibold flex'>
                        {comboProduct?.product_name} {Boolean(comboPrice) && <div className='text-gray-500 text-xs font-normal pl-2'>{`+${formatRupiah(comboPrice)}`}</div>}
                      </div>
                      <input
                        id={`${index}_${idx}_${combo.combo_id}`}
                        type={'radio'}
                        value={comboProduct?.product_id}
                        name={`combo_${index}`}
                        className='w-4 h-4 bg-white !border-[var(--byzantium)] after:!bg-[var(--byzantium)] focus:!shadow-none'
                        onChange={(e) => {
                          setFieldValue(`${index}.productDetails`, [comboProduct]);
                        }}
                        checked={isSelected}
                      />
                    </label>
									);
								})}
							</div>
						);
					})}
				</div>
				<div className='mt-5'>
					<Notes item_id={item?.itemID ? item?.itemID : ''} callback={setNote} notes={note} />
				</div>
			</div>
			<div className='bg-white w-full p-4 z-auto shadow-custom'>
				<div className='flex justify-between items-center'>
					<div className='text-gray-900 text-xs font-semibold'>Jumlah Item</div>
					<div className='bg-white p-1 rounded-md cursor-pointer flex items-center border-1 border-solid border-[var(--byzantium)] text-xs'>
						<MinusIcon
							className='h-5 w-5 text-[var(--byzantium)]'
							onClick={() =>
								setTotal((prev) => {
									if (prev === 0) return 0;
									return prev - 1;
								})
							}
						/>
						<input
							type='text'
							className='!bg-white !m-0 !p-0 !h-5 !w-8 text-center text-black font-semibold text-xs opacity-100'
							value={total}
							disabled
							onChange={(e) => {
								const value = e.target.value;
								const num = value.replace(/[^0-9]/g, '');
								setTotal(num);
							}}
						/>
						<PlusIcon className='h-5 w-5 text-[var(--byzantium)]' onClick={() => setTotal((prev) => prev + 1)} />
					</div>
				</div>
				<button
					onClick={async () => {
						if (!isEdit && item) return;
						console.log('handleClick button ComboSummary');
						sendAnalyticsEvent('Add To Cart', 'merchant_add_to_cart', product?.product_name);
						
						const isValid = await validateComboStepByStep();
						if (!isValid) return;

						handleSubmitFormik();
					}}
					type='button'
					id='merchant_add_to_cart'
					disabled={!total}
					className={`mt-2 w-full disabled:cursor-not-allowed  ${!total ? 'bg-gray-400' : 'bg-[var(--rose-violet)]'} text-white flex justify-center font-normal text-xs p-3 items-center border-0`}>
					{isEdit ? 'Perbarui Keranjang' : 'Tambah ke Keranjang'}
				</button>
			</div>
		</>
	);
};

const ComboSummary = ({ item = {}, handleSubmit = (data, isClose) => {}, handleEdit = (item) => {} }) => {
	console.log('ComboSummary');

  const total = Number(item?.itemQuantity || 0);
  const note = item?.itemNotes || '';
  const comboSet = 
      item?.comboset?.map((combo) => ({
        comboSetId: combo?.comboSetId,
        comboSetname: combo?.comboSetname,
        productDetails: (combo?.productDetails || combo?.products)?.map((detail) => ({
          productPrimaryID: detail?.product_primary_id,
          productID: detail?.productID,
          productName: detail?.productName,
          productSKU: detail?.productSKU,
          productPrice: Number(detail?.productPrice) || 0,
          quantity: 1,
        })),
      })) || [];

  const totalPrice = useMemo(() => {
		const comboPrice = comboSet?.reduce((sum, comb) => {
			const detailPriceSum = comb?.productDetails?.reduce((detailSum, detail) => {
				return detailSum + Number(detail?.productPrice || 0);
			}, 0);
			return sum + detailPriceSum;
		}, 0);

		const productPrice = Number(item?.itemUnitPrice);
		return (productPrice + comboPrice) * total;
	}, [comboSet, total]);

	const updateTotal = (type, item) => {
    let nextTotal = total;
		if (type === 'plus') {
			nextTotal += 1;
		} else {
			nextTotal = nextTotal > 0 ? nextTotal - 1 : 0;
		}

		const dataPost = {
			productID: item?.productID,
			quantity: nextTotal,
			type: 'update',
			productType: 'Combo',
			specialNotes: item?.itemNotes,
			comboset: item?.comboset?.map((combo) => ({
        comboSetId: combo?.comboSetId,
        comboSetname: combo?.comboSetname,
        productDetails: combo?.productDetails?.map((detail) => ({
          productPrimaryID: detail?.product_primary_id,
          productID: detail?.productID,
          productName: detail?.productName,
          productSKU: detail?.productSKU,
          productPrice: Number(detail?.productPrice) || 0,
          quantity: 1,
        })),
      })) || [],
			itemID: item?.itemID,
      productPrimaryID: item?.product_primary_id
		};
    handleSubmit(dataPost, false);
	};

	return (
		<div key={`combo_${item?.itemID}`} className='mt-4'>
			{item?.comboset?.map((combo, index) => {
				return (
					<div key={index} className='w-full flex items-center pb-1'>
						<div className='text-sm text-gray-900 font-semibold min-w-fit'>{combo?.comboSetname}:</div>
						<div className='flex w-full flex-col'>
							{combo?.productDetails?.map((comboProduct, idx) => {
								return (
									<div key={`${index}_${idx}`} className='flex items-start pl-2'>
										<div key={`${index}_${idx}`} className='text-xs font-normal text-gray-800'>
											{comboProduct?.productName ?? ''}
										</div>
										{Boolean(Number(comboProduct?.productPrice || 0)) && <div className='text-gray-500 text-xs font-normal pl-1'>{`+${formatRupiah(comboProduct?.productPrice)}`}</div>}
									</div>
								);
							})}
						</div>
					</div>
				);
			})}
			<div className='w-full flex mt-1 items-center'>
				<div className='text-sm font-semibold text-gray-800'>Notes:</div>
				<div className='text-xs font-normal text-gray-800 pl-2'>{note ?? ''}</div>
			</div>
			<div className='text-sm text-gray-900 font-semibold mt-1'>{formatRupiah(totalPrice || 0)}</div>
			<div className='flex justify-between mt-2'>
				<div
					className='flex border-1 border-solid border-gray-300 rounded-full py-1 px-2 items-center'
					onClick={() => {
						handleEdit(item);
					}}>
					<PencilEditIcon />
					<div className='pl-2 text-gray-900 text-xs font-semibold'>Edit</div>
				</div>
				<div className=''>
					<div className='bg-white p-1 rounded-md cursor-pointer flex items-center border-solid border-1 border-[var(--byzantium)]'>
						<MinusIcon className='h-5 w-5 text-[var(--byzantium)]' onClick={() => updateTotal('minus', item)} />
						<input type='text' className='!bg-white !m-0 !p-0 !h-5 !w-8 text-center !text-[var(--byzantium)] font-normal text-xs' value={total} disabled />
						<PlusIcon className='h-5 w-5 text-[var(--byzantium)]' onClick={() => updateTotal('plus', item)} />
					</div>
				</div>
			</div>
		</div>
	);
};

const ProductCombo = ({ product = {}, cart = null, handleCart = (data) => {}, setCartItem = (data) => {}, edit = false }) => {
	console.log('ProductCombo');
  const currentCartItem = [...(cart?.item || [])].filter((item) => item.productID === product?.product_id).sort((a, b) => Number(a.itemID) - Number(b.itemID));

	const desc = removeHTML(product?.short_description || '');
	const [item, setItem] = useState(null);
	const [isAddMore, setIsAddMore] = useState(false);
	const [isEdit, setIsEdit] = useState(false);

	const handleSubmit = (data = {}, isClose = true) => {
		console.log('handleSubmit');
		handleCart(data, isClose);
	};

	const handleEdit = (item = {}) => {
		console.log('handleEdit');
		setItem(item);
		setIsEdit(true);
	};

	const handleAddMore = () => {
		console.log('handleAddMore');
		setIsAddMore(true);
	};

	return (
		<>
			{currentCartItem?.length && !isAddMore && !isEdit ? (
				<>
					<div className={`bg-white rounded-t-xl p-4 shadow-custom transition-transform duration-500 ease-in-out ${true ? 'translate-y-0 relative' : 'translate-y-full hidden'}`}>
						<div className='hidden justify-center'>
							<div className='bg-gray-300 h-1.5 w-10 rounded-full' />
						</div>
						<div className='mt-4 text-gray-900 font-semibold text-sm'>{product?.product_name || ''}</div>
						<div className='text-gray-400 text-xs pt-2 font-normal'>{desc}</div>
						<div className='mt-5'>
							{currentCartItem?.map((item, index) => {
								return <ComboSummary key={item?.itemID} item={item} handleSubmit={handleSubmit} handleEdit={handleEdit} />;
							})}
						</div>
					</div>
					<div className='bg-white w-full p-4 z-auto shadow-custom'>
						<button onClick={handleAddMore} className={`mt-2 w-full disabled:cursor-not-allowed bg-[var(--byzantium)] text-white flex justify-center font-normal text-xs p-3 items-center border-0`}>
							{'Tambah Pesanan'}
						</button>
					</div>
				</>
			) : (
				<ComboDetail product={product} item={item} isEdit={isEdit} handleSubmitItem={handleSubmit} />
			)}
		</>
	);
};

export default ProductCombo;
